import { useState } from 'react';
import { FILTER_CHIP_ACTIVE_CLASS, FILTER_CHIP_CLASS, FILTER_CHIP_INACTIVE_CLASS } from '../catalog/filterChipStyles';
import { useVariationCatalog } from '../../hooks/useVariationCatalog';
import { VariationCatalogOption } from '../../utils/variationCatalog';

const AGE_TYPE_ID = 0;
const STORE_MAX_AGE_YEARS = 14;
const STORE_MAX_AGE_MONTHS = STORE_MAX_AGE_YEARS * 12 + 11;
const LEADING_NUMBER_REGEX = /^\d+/;

function isWithinStoreAgeRange(option: VariationCatalogOption): boolean {
  const label = option.label.trim().toLowerCase();
  const leading = LEADING_NUMBER_REGEX.exec(label) ?? LEADING_NUMBER_REGEX.exec(option.code.trim());
  const labelledInYears = !label.includes('mes') && leading !== null;
  if (labelledInYears && Number(leading[0]) > STORE_MAX_AGE_YEARS) return false;
  if (option.minAgeMonths !== null) return option.minAgeMonths <= STORE_MAX_AGE_MONTHS;
  return true;
}

interface VariationFilterProps {
  selectedTypeId: number | undefined;
  onTypeChange: (typeId: number | undefined) => void;
  values: string[];
  onChange: (values: string[]) => void;
  onSelect: (typeId: number, values: string[]) => void;
}

export function VariationFilter({ selectedTypeId, onTypeChange, values, onChange, onSelect }: VariationFilterProps) {
  const { types, isEmpty } = useVariationCatalog();
  const [showOthers, setShowOthers] = useState(false);

  if (isEmpty) return null;

  const defaultType = types.find((t) => t.id === AGE_TYPE_ID) ?? types[0];
  const activeType = types.find((t) => t.id === selectedTypeId) ?? defaultType;
  const otherTypes = types.filter((t) => t.id !== defaultType.id);
  const othersOpen = showOthers || (selectedTypeId !== undefined && selectedTypeId !== defaultType.id);

  const handleValueToggle = (code: string) => {
    const base = selectedTypeId === activeType.id ? values : [];
    const next = base.includes(code) ? base.filter((v) => v !== code) : [...base, code];
    if (selectedTypeId === activeType.id) {
      onChange(next);
    } else {
      onSelect(activeType.id, next);
    }
  };

  const handleTypeSelect = (typeId: number) => {
    onTypeChange(selectedTypeId === typeId ? undefined : typeId);
  };

  const isOptionActive = (code: string) => selectedTypeId === activeType.id && values.includes(code);
  const visibleOptions =
    activeType.id === AGE_TYPE_ID
      ? activeType.options.filter((option) => isWithinStoreAgeRange(option) || isOptionActive(option.code))
      : activeType.options;

  return (
    <div>
      <p className="text-sm text-text-medium mb-2 lg:mb-1.5">{activeType.name}</p>
      <div className="flex flex-wrap gap-1.5">
        {visibleOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={isOptionActive(option.code)}
            onClick={() => handleValueToggle(option.code)}
            className={[FILTER_CHIP_CLASS, isOptionActive(option.code) ? FILTER_CHIP_ACTIVE_CLASS : FILTER_CHIP_INACTIVE_CLASS].join(' ')}
          >
            {option.label}
          </button>
        ))}
      </div>

      {otherTypes.length > 0 && (
        <div className="mt-3 lg:mt-1.5">
          <button
            type="button"
            aria-expanded={othersOpen}
            onClick={() => setShowOthers((v) => !v)}
            className="inline-flex items-center min-h-[44px] lg:min-h-0 text-sm text-chocolate-dark font-medium hover:text-chocolate transition-colors"
          >
            {othersOpen ? 'Menos opções' : 'Outras opções'}
          </button>
          {othersOpen && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {otherTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  aria-pressed={selectedTypeId === type.id}
                  onClick={() => handleTypeSelect(type.id)}
                  className={[FILTER_CHIP_CLASS, selectedTypeId === type.id ? FILTER_CHIP_ACTIVE_CLASS : FILTER_CHIP_INACTIVE_CLASS].join(' ')}
                >
                  {type.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
